/**
 * MarketFit Situation Definition Page
 * Form tab + Agent tab for defining a situation (assets, needs, inputs, outputs, actors).
 * Route: /dashboard/product/:productId/marketfit/situation/:situationId
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tabs, Button, Input, Typography, Spin, message } from 'antd';
import { BulbOutlined, RobotOutlined, FormOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  getMarketFitSituation,
  updateMarketFitSituation,
  listMarketFitActors,
  createMarketFitActor,
  type MarketFitActor,
} from '../services/localBridgeSessionApi';
import {
  parseSituationDetails,
  parseSituationSchemaV2,
  getMarketFitSchemaVersion,
  defaultSituationSchemaV2,
  generateId,
  type SituationDetails,
  type SituationSchemaV2,
  type SituationAssetItem,
  type SituationNeedItem,
  type SituationInputItem,
  type SituationOutputItem,
} from '../types/marketFitLocalBridge';
import { SituationDefinitionForm } from '../components/MarketFit/SituationDefinitionForm';
import MarketFitAgentChat from '../components/MarketFit/MarketFitAgentChat';
import { useUser } from '../contexts/UserContext';
import { getProductDomainSession } from '../services/productDomainSessionApi';

/** Merge agent-extracted v2 data into SituationSchemaV2 */
function mergeAgentDataIntoDetailsV2(
  current: SituationSchemaV2,
  agentData: Partial<SituationSchemaV2> & Record<string, unknown>,
  doMerge?: boolean
): SituationSchemaV2 {
  const result = { ...current };
  if (Array.isArray(agentData.value_outputs) && agentData.value_outputs.length > 0) {
    result.value_outputs = doMerge ? [...current.value_outputs, ...agentData.value_outputs] : agentData.value_outputs;
  }
  if (Array.isArray(agentData.costs) && agentData.costs.length > 0) {
    result.costs = doMerge ? [...current.costs, ...agentData.costs] : agentData.costs;
  }
  if (Array.isArray(agentData.constraints) && agentData.constraints.length > 0) {
    result.constraints = doMerge ? [...current.constraints, ...agentData.constraints] : agentData.constraints;
  }
  if (Array.isArray(agentData.assets) && agentData.assets.length > 0) {
    result.assets = doMerge ? [...current.assets, ...agentData.assets] : agentData.assets;
  }
  if (agentData.currency_value_ratio != null) {
    result.currency_value_ratio = agentData.currency_value_ratio as SituationSchemaV2['currency_value_ratio'];
  }
  return result;
}

const { Title, Text } = Typography;

/** Merge agent-extracted data into SituationDetails (legacy schema) */
function mergeAgentDataIntoDetails(
  current: SituationDetails,
  agentData: Record<string, unknown>
): SituationDetails {
  const toAsset = (x: unknown): SituationAssetItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
        o.category as 'cost'
      )
        ? (o.category as 'cost')
        : 'cost',
    };
  };

  const toNeed = (x: unknown): SituationNeedItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
        o.category as 'cost'
      )
        ? (o.category as 'cost')
        : 'cost',
    };
  };

  const toInput = (x: unknown): SituationInputItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
        o.category as 'cost'
      )
        ? (o.category as 'cost')
        : 'cost',
    };
  };

  const toOutput = (x: unknown): SituationOutputItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
        o.category as 'cost'
      )
        ? (o.category as 'cost')
        : 'cost',
    };
  };

  const assets = Array.isArray(agentData.assets)
    ? (agentData.assets as unknown[]).map(toAsset).filter((a) => a.name?.trim())
    : current.assets;
  const needs = Array.isArray(agentData.needs)
    ? (agentData.needs as unknown[]).map(toNeed).filter((n) => n.name?.trim())
    : current.needs;
  const inputs = Array.isArray(agentData.inputs)
    ? (agentData.inputs as unknown[]).map(toInput).filter((i) => i.name?.trim())
    : current.inputs;
  const outputs = Array.isArray(agentData.outputs)
    ? (agentData.outputs as unknown[]).map(toOutput).filter((o) => o.name?.trim())
    : current.outputs;

  return {
    ...current,
    assets: assets.length > 0 ? assets : current.assets,
    needs: needs.length > 0 ? needs : current.needs,
    inputs: inputs.length > 0 ? inputs : current.inputs,
    outputs: outputs.length > 0 ? outputs : current.outputs,
  };
}

export const MarketFitSituationPage: React.FC = () => {
  const { productId, situationId } = useParams<{ productId: string; situationId: string }>();
  const navigate = useNavigate();
  const { selectedCompany } = useUser();

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [situationName, setSituationName] = useState('');
  const [data, setData] = useState<SituationDetails>({
    assets: [],
    needs: [],
    inputs: [],
    outputs: [],
    actors: [],
  });
  const [actors, setActors] = useState<MarketFitActor[]>([]);
  const [schemaMode, setSchemaMode] = useState<'legacy' | 'v2'>('legacy');
  const [dataV2, setDataV2] = useState<SituationSchemaV2>(defaultSituationSchemaV2());
  const [activeTab, setActiveTab] = useState<string>('form');

  // Create flow state (when situationId === 'new')
  const [newSituationName, setNewSituationName] = useState('');
  const [creating, setCreating] = useState(false);

  const isNew = situationId === 'new';

  const resolveCompanyId = useCallback(async (): Promise<string | null> => {
    if (selectedCompany?.id) return selectedCompany.id;
    if (productId) {
      const session = await getProductDomainSession(productId);
      return session?.company_id ?? null;
    }
    return null;
  }, [selectedCompany?.id, productId]);

  const loadSituationAndActors = useCallback(async () => {
    if (!situationId || isNew) return;
    setLoading(true);
    try {
      const cid = await resolveCompanyId();
      setCompanyId(cid);
      if (!cid) {
        setLoading(false);
        return;
      }
      const [s, actorList] = await Promise.all([
        getMarketFitSituation(situationId),
        listMarketFitActors(cid),
      ]);
      if (s) {
        setSituationName(s.name);
        const detailsJson = s.details_json ?? '';
        setData(parseSituationDetails(detailsJson));
        setDataV2(parseSituationSchemaV2(detailsJson));
        setSchemaMode(getMarketFitSchemaVersion(detailsJson) === 2 ? 'v2' : 'legacy');
      } else {
        setSituationName('');
        setData({ assets: [], needs: [], inputs: [], outputs: [], actors: [] });
        setDataV2(defaultSituationSchemaV2());
        setSchemaMode('legacy');
      }
      setActors(actorList);
    } finally {
      setLoading(false);
    }
  }, [situationId, isNew, resolveCompanyId]);

  useEffect(() => {
    if (isNew) {
      resolveCompanyId().then(setCompanyId);
      setLoading(false);
    } else {
      loadSituationAndActors();
    }
  }, [isNew, loadSituationAndActors, resolveCompanyId]);

  const handleSave = async () => {
    if (!situationId || isNew) return;
    const details_json =
      schemaMode === 'v2'
        ? JSON.stringify(dataV2)
        : JSON.stringify({
            assets: data.assets.filter((a) => a.name?.trim()),
            needs: data.needs.filter((n) => n.name?.trim()),
            inputs: data.inputs.filter((i) => i.name?.trim()),
            outputs: data.outputs.filter((o) => o.name?.trim()),
            actors: data.actors,
          });
    setSaving(true);
    try {
      const updated = await updateMarketFitSituation(situationId, { details_json });
      if (updated) {
        message.success('Situation definition saved');
      } else {
        message.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateActor = useCallback(
    async (name: string) => {
      if (!companyId) return;
      const created = await createMarketFitActor({ company_id: companyId, actor_name: name });
      if (created) {
        setActors((prev) => [...prev, created]);
        setData((prev) => ({
          ...prev,
          actors: [{ actor_id: created.id, is_primary: true }],
        }));
        message.success('Actor created');
      } else {
        message.error('Failed to create actor');
      }
    },
    [companyId]
  );

  const handleApplyFromAgent = useCallback(
    (agentData: Record<string, unknown>, options?: { merge?: boolean }) => {
      const isV2 =
        (agentData as { schema_version?: number }).schema_version === 2 ||
        (Array.isArray(agentData.value_outputs) || Array.isArray(agentData.costs) || Array.isArray(agentData.constraints));
      if (schemaMode === 'v2' && isV2) {
        setDataV2((prev) => mergeAgentDataIntoDetailsV2(prev, agentData as Partial<SituationSchemaV2>, options?.merge));
      } else {
        setData((prev) => mergeAgentDataIntoDetails(prev, agentData));
      }
      setActiveTab('form');
      message.success('Agent suggestions applied to form');
    },
    [schemaMode]
  );

  const handleCreateSituation = async () => {
    const name = newSituationName?.trim();
    if (!name || !companyId) {
      message.warning('Enter a situation name');
      return;
    }
    setCreating(true);
    try {
      const { createMarketFitSituation } = await import('../services/localBridgeSessionApi');
      const created = await createMarketFitSituation({ company_id: companyId, name });
      if (created && productId) {
        message.success('Situation created');
        navigate(`/dashboard/product/${productId}/marketfit/situation/${created.id}`, {
          replace: true,
        });
      } else {
        message.error('Failed to create situation');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleBack = () => {
    if (productId) {
      navigate(`/dashboard/product/${productId}/marketfit`);
    } else {
      navigate('/dashboard');
    }
  };

  if (!productId) {
    return (
      <Card>
        <Text type="secondary">No product selected.</Text>
        <Button type="link" onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </Card>
    );
  }

  if (isNew) {
    return (
      <Card
        title={
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back
          </Button>
        }
      >
        <Title level={4}>Create situation</Title>
        <p>Enter a name for the new situation.</p>
        <div style={{ marginTop: 16, maxWidth: 400 }}>
          <Input
            placeholder="Situation name"
            value={newSituationName}
            onChange={(e) => setNewSituationName(e.target.value)}
            onPressEnter={handleCreateSituation}
          />
          <Button
            type="primary"
            loading={creating}
            onClick={handleCreateSituation}
            style={{ marginTop: 12 }}
          >
            Create
          </Button>
        </div>
        {!companyId && (
          <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
            Select a company in the dashboard to create a situation.
          </Text>
        )}
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <Spin spinning tip="Loading situation...">
          <div style={{ padding: 48, textAlign: 'center', minHeight: 100 }} />
        </Spin>
      </Card>
    );
  }

  if (!companyId) {
    return (
      <Card>
        <Text type="secondary">Company context required. Select a company in the dashboard.</Text>
        <Button type="link" onClick={handleBack}>
          Go back
        </Button>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <BulbOutlined />
          <span>Define situation: {situationName || '…'}</span>
        </div>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyOnHidden={false}
        items={[
          {
            key: 'form',
            label: (
              <span>
                <FormOutlined /> Form
              </span>
            ),
            children: (
              <SituationDefinitionForm
                data={data}
                dataV2={dataV2}
                schemaMode={schemaMode}
                onDataChange={setData}
                onDataV2Change={setDataV2}
                onSchemaModeChange={setSchemaMode}
                onSave={handleSave}
                loading={loading}
                saving={saving}
                actors={actors}
                companyId={companyId}
                situationId={situationId ?? null}
                onCreateActor={handleCreateActor}
                showFooter={true}
              />
            ),
          },
          {
            key: 'agent',
            label: (
              <span>
                <RobotOutlined /> Agent
              </span>
            ),
            children: (
              <div style={{ marginTop: 16 }}>
                <MarketFitAgentChat
                  artifactType="situation"
                  artifactId={situationId ?? null}
                  companyId={companyId}
                  onApplyToForm={handleApplyFromAgent}
                  stateId="situation_definition"
                  requiredFields={['assets', 'needs', 'inputs', 'outputs']}
                  userPrompt="Describe the situation. What assets, needs, inputs, and outputs are involved?"
                  formData={
                    schemaMode === 'v2'
                      ? dataV2
                      : { assets: data.assets, needs: data.needs, inputs: data.inputs, outputs: data.outputs, actors: data.actors }
                  }
                />
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default MarketFitSituationPage;
